import React, { useState, useEffect, useRef } from 'react';
import { auth, database } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { ref, set, update, onValue, get } from 'firebase/database';
import { useNavigate, Link } from 'react-router-dom';
import WaitlistForm from './WaitlistForm'; // Import the WaitlistForm component

const LoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(true); // Changed from false to true
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authSuccess, setAuthSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [accountCreationDisabled, setAccountCreationDisabled] = useState(false);
  // New state for Android waitlist modal
  const [showAndroidModal, setShowAndroidModal] = useState(false);
  const [showWaitlistSuccess, setShowWaitlistSuccess] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const navigate = useNavigate();
  const passwordRef = useRef();
  const confirmPasswordRef = useRef();
  const [isForwardingToiOS, setIsForwardingToiOS] = useState(false);

  const APP_STORE_URL = "https://apps.apple.com/us/app/forecast-financial-planner/id6742382846";
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.tryforecast.app&pcampaignid=web_share";

  // Add this new useEffect for iOS auto-forward (place it after your existing useEffects)
  useEffect(() => {
    if (isOpen) {
      // Never auto-forward on legal pages (Apple App Review needs to see them)
      if (isOnLegalPage()) return;

      // Attempt iOS auto-forward
      const wasForwardedToiOS = autoForwardToAppStore(APP_STORE_URL);

      // Attempt Android auto-forward (only if not iOS)
      const wasForwardedToAndroid = !wasForwardedToiOS && autoForwardToPlayStore(PLAY_STORE_URL);
      
      if (wasForwardedToiOS) {
        setIsForwardingToiOS(true);
        setTimeout(() => {
          setIsForwardingToiOS(false);
        }, 2000);
      } else if (wasForwardedToAndroid) {
        setIsForwardingToiOS(true); // Reuse the same loading state
        setTimeout(() => {
          setIsForwardingToiOS(false);
        }, 2000);
      }
    }
  }, [isOpen]);
  // Check if device is mobile
  useEffect(() => {
    const checkDevice = () => {
      const mobileCheck = window.innerWidth <= 768;
      setIsMobile(mobileCheck);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Check if account creation is disabled due to subscriber threshold
  useEffect(() => {
    if (isOpen) {
      checkAccountCreationStatus();
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setError('');
      setAuthSuccess(false);
      setIsSigningUp(true); // Reset to create account when modal opens
      setShowAndroidModal(false);
      setShowWaitlistSuccess(false);
      setShowAuthForm(false);
    }
  }, [isOpen]);

  // Function to check if account creation is allowed
  const checkAccountCreationStatus = async () => {
    try {
      // First check settings/accountCreation
      const settingsRef = ref(database, 'settings/accountCreation');
      const settingsSnapshot = await get(settingsRef);
      
      if (settingsSnapshot.exists() && settingsSnapshot.val().enabled === false) {
        // Account creation has been explicitly disabled
        setAccountCreationDisabled(true);
        return;
      }
      
      // If no explicit setting, check subscriber count
      const subscribersRef = ref(database, 'metrics/subscribers');
      const subscribersSnapshot = await get(subscribersRef);
      
      if (subscribersSnapshot.exists()) {
        const subscriberData = subscribersSnapshot.val();
        const subscriberThreshold = 1000; // Same as in webhook
        console.log(subscriberData.activeCount)
        if (subscriberData.activeCount >= subscriberThreshold) {
          setAccountCreationDisabled(true);
        } else {
          setAccountCreationDisabled(false);
        }
      } else {
        // No metrics found, allow account creation
        setAccountCreationDisabled(false);
      }
    } catch (error) {
      console.error('Error checking account creation status:', error);
      // Default to allowing creation if there's an error checking
      setAccountCreationDisabled(false);
    }
  };

  const initializeUserData = async (userId, userEmail) => {
    // Get current timestamp for account creation date
    const accountCreationDate = new Date().toISOString();
    
    // Set basic user properties
    await set(ref(database, `users/${userId}/subscriptionStatus`), 'inactive');
    await set(ref(database, `users/${userId}/needsOnboarding`), true);
    await set(ref(database, `users/${userId}/hasSeenWelcomeModal`), false);
    
    // Set profile setup flag
    await set(ref(database, `users/${userId}/profileSetup`), true);
    
    // Store account creation date and email
    await set(ref(database, `users/${userId}/accountCreationDate`), accountCreationDate);
    await set(ref(database, `users/${userId}/email`), userEmail);
    
    console.log('User data initialized successfully with creation date and email');
  };

  // Handle trial start - shows auth form and hides download section
  const handleStartTrial = () => {
    setShowAuthForm(true);
    setIsSigningUp(false); // Default to login for trial
  };
// iOS Detection Function
const detectiOS = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';
  
  const isiOSUserAgent = /iphone|ipad|ipod/.test(userAgent);
  const isiOSPlatform = /iphone|ipad|ipod|mac/.test(platform) && 'ontouchstart' in window;
  const isiOSStandalone = window.navigator.standalone !== undefined;
  const isiOSWebkit = /webkit/.test(userAgent) && !/chrome|android/.test(userAgent);
  
  return isiOSUserAgent || isiOSPlatform || isiOSStandalone || 
         (isiOSWebkit && /safari/.test(userAgent));
};

// Legal pages that should never auto-redirect (Apple App Review requirement)
const LEGAL_PAGES = ['/terms-of-service', '/privacy-policy'];

const isOnLegalPage = () => LEGAL_PAGES.includes(window.location.pathname);

// Auto-Forward Function
const autoForwardToAppStore = (appStoreURL) => {
  // Skip on legal pages — Apple needs to see these during review
  if (isOnLegalPage()) return false;

  // Skip in development
  if (window.location.hostname === 'localhost' ||
      window.location.hostname.includes('dev') ||
      window.location.hostname.includes('staging')) {
    console.log('Development environment - skipping iOS auto-forward');
    return false;
  }

  // Skip if we've already forwarded this session
  if (sessionStorage.getItem('ios_forwarded')) {
    return false;
  }

  // Detect iOS and forward
  if (detectiOS()) {
    console.log('iOS detected - forwarding to App Store in 1.5 seconds...');

    // Mark as forwarded
    sessionStorage.setItem('ios_forwarded', 'true');

    // Forward after showing modal briefly
    setTimeout(() => {
      window.location.href = appStoreURL;
    }, 1500);

    return true;
  }

  return false;
};

const autoForwardToPlayStore = (playStoreURL) => {
  // Skip on legal pages — Apple needs to see these during review
  if (isOnLegalPage()) return false;

  // Skip in development
  if (window.location.hostname === 'localhost' ||
      window.location.hostname.includes('dev') ||
      window.location.hostname.includes('staging')) {
    console.log('Development environment - skipping Android auto-forward');
    return false;
  }

  // Skip if we've already forwarded this session
  if (sessionStorage.getItem('android_forwarded')) {
    return false;
  }

  // Detect Android and forward
  if (detectAndroid()) {
    console.log('Android detected - forwarding to Play Store in 1.5 seconds...');

    // Mark as forwarded
    sessionStorage.setItem('android_forwarded', 'true');

    // Forward after showing modal briefly
    setTimeout(() => {
      window.location.href = playStoreURL;
    }, 1500);

    return true;
  }

  return false;
};
  // Download handlers
  const handleIOSDownload = () => {
    // Redirect to iOS App Store
    window.open("https://apps.apple.com/us/app/forecast-financial-planner/id6742382846", "_blank");
  };

  const handleAndroidDownload = () => {
    // Direct redirect to Google Play Store instead of showing waitlist
    window.open("https://play.google.com/store/apps/details?id=com.tryforecast.app", "_blank");
  };

  const handleAddToWaitlist = async () => {
    try {
      // For users not logged in, we'll store their email from the form or ask for it
      const userEmail = email || prompt("Please enter your email to join the Android waitlist:");
      
      if (!userEmail) {
        return; // User cancelled
      }
      
      // Add user to the waitlist in Firebase using email as key
      const emailKey = userEmail.replace(/[.#$[\]]/g, '_'); // Firebase key-safe email
      const waitlistRef = ref(database, `waitlist/android/${emailKey}`);
      await set(waitlistRef, {
        email: userEmail,
        timestamp: Date.now(),
        platform: 'android',
        source: 'login_modal'
      });
      
      // Close the modal and show success message
      setShowAndroidModal(false);
      setShowWaitlistSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowWaitlistSuccess(false);
      }, 3000);
      
      console.log("User added to Android waitlist:", userEmail);
    } catch (error) {
      console.error("Error adding to waitlist:", error);
    }
  };
  const detectAndroid = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
  };
  const handleCreateAccount = async () => {
    // Check if account creation is disabled
    if (accountCreationDisabled) {
      setError('We have reached our capacity for new accounts. Please try again later or join our waitlist.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
  
    try {
      setLoading(true);
      setError('');
      
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Initialize user data in database with welcome modal flag set to false
      // Pass the email to store it in the user data
      await initializeUserData(user.uid, email);
      
      // Wait for data to be confirmed as written
      const userRef = ref(database, `users/${user.uid}`);
      await new Promise((resolve, reject) => {
        let unsubscribe;
        try {
          unsubscribe = onValue(userRef, (snapshot) => {
            if (snapshot.exists()) {
              if (unsubscribe) {
                unsubscribe();
              }
              resolve();
            }
          }, (error) => {
            if (unsubscribe) {
              unsubscribe();
            }
            reject(error);
          });
        } catch (error) {
          reject(error);
        }
      });
      
      console.log('Account created successfully');
      setLoading(false);
      
      // Show post-auth options instead of closing
      setAuthSuccess(true);
      
      // Call onAuthSuccess if provided
      if (typeof onAuthSuccess === 'function') {
        onAuthSuccess();
      }
      
    } catch (error) {
      setLoading(false);
      console.error('Account creation error:', error.code, error.message);
      setError(error.message);
    }
  };

  const handleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user exists in DB, if not initialize them
      const userRef = ref(database, `users/${user.uid}`);
      await new Promise((resolve, reject) => {
        let unsubscribe;
        try {
          unsubscribe = onValue(userRef, (snapshot) => {
            if (!snapshot.exists()) {
              // If user doesn't exist in the database yet, initialize them
              // For existing users signing in, we also store their email and creation date
              initializeUserData(user.uid, user.email || email).then(() => {
                if (unsubscribe) unsubscribe();
                resolve();
              }).catch(reject);
            } else {
              // User exists, check if we need to add missing fields
              const userData = snapshot.val();
              const updates = {};
              
              // Add email if missing
              if (!userData.email && (user.email || email)) {
                updates[`users/${user.uid}/email`] = user.email || email;
              }
              
              // Add account creation date if missing (use current date as fallback)
              if (!userData.accountCreationDate) {
                updates[`users/${user.uid}/accountCreationDate`] = new Date().toISOString();
              }
              
              // Apply updates if any
              if (Object.keys(updates).length > 0) {
                update(ref(database), updates).then(() => {
                  if (unsubscribe) unsubscribe();
                  resolve();
                }).catch(reject);
              } else {
                if (unsubscribe) unsubscribe();
                resolve();
              }
            }
          }, (error) => {
            if (unsubscribe) unsubscribe();
            reject(error);
          });
        } catch (error) {
          reject(error);
        }
      });
      
      setLoading(false);
      
      // Show post-auth options instead of closing
      setAuthSuccess(true);
      
      // Call onAuthSuccess if provided
      if (typeof onAuthSuccess === 'function') {
        onAuthSuccess();
      }
      
    } catch (error) {
      setLoading(false);
      console.error('Sign in error:', error.code, error.message);
      setError(error.message);
    }
  };

  // Handle post-authentication navigation
  const handleContinueToWeb = () => {
    onClose(); // Close the modal
    navigate('/calendar'); // Navigate to the web app
  };

  const handleContinueToMobile = () => {
    onClose(); // Close the modal
    // Could redirect to app store or show download instructions
    navigate('/dashboard'); // For now, navigate to dashboard where download button exists
  };

  const handleStartFreeTrial = () => {
    onClose(); // Close the modal
    navigate('/dashboard'); // Navigate to dashboard for subscription management
  };

  if (!isOpen) return null;

  const LoadingComponent = () => (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingSpinner}></div>
      <p style={{ marginTop: '16px', color: '#64748B', fontSize: '14px', textAlign: 'center' }}>
        {isForwardingToiOS ? 'Redirecting to App Store...' : 'Loading...'}
      </p>
    </div>
  );

  // Post-authentication success screen
  const AuthSuccessComponent = () => (
    <div style={styles.successContainer}>
      <h2 style={styles.successHeading}>
        {isSigningUp ? 'Account Created Successfully! 🎉' : 'Welcome Back! 👋'}
      </h2>
      
      <p style={styles.successText}>
        {isSigningUp 
          ? 'Your Forecast account has been created. Choose how you want to continue:'
          : 'You\'re logged in. Choose how you want to continue:'}
      </p>
      
      <div style={styles.buttonContainer}>
        {/* Primary button based on device type */}
        <button 
          style={styles.primaryButton} 
          onClick={isMobile ? handleContinueToMobile : handleContinueToWeb}
        >
          <span style={styles.buttonText}>
            {isMobile 
              ? 'Continue to Forecast App' 
              : 'Continue to Forecast Web'}
          </span>
        </button>
        
        {/* Secondary option */}
        <button 
          style={styles.secondaryButton} 
          onClick={isMobile ? handleContinueToWeb : handleContinueToMobile}
        >
          <span style={styles.secondaryButtonText}>
            {isMobile 
              ? 'Use Web Version Instead' 
              : 'Get Mobile App Instead'}
          </span>
        </button>
        
        {/* Start trial button */}
        <button 
          style={styles.trialButton} 
          onClick={handleStartFreeTrial}
        >
          <span style={styles.buttonText}>
            Start Free Trial
          </span>
        </button>
      </div>
    </div>
  );

// Download buttons component
const DownloadButtonsComponent = () => (
  <div style={styles.downloadSection}>
    <h3 style={styles.downloadHeading}>Get Forecast</h3>
    <p style={styles.downloadText}>
    Download the app or log in to manage your subscription.</p>
    
    {/* Waitlist Success Message */}
    {showWaitlistSuccess && (
      <div style={styles.waitlistSuccessMessage}>
        You've been added to the Android waitlist!
      </div>
    )}
    
    <div style={styles.downloadButtonContainer}>
      {/* iOS Download Button */}
      <button
        onClick={handleIOSDownload}
        style={styles.downloadButton}
      >
        {/* Apple Logo SVG */}
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "0.5rem" }}
        >
          <path 
            d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09998 22C7.78998 22.05 6.79998 20.68 5.95998 19.47C4.24998 17 2.93998 12.45 4.69998 9.39C5.56998 7.87 7.12998 6.91 8.81998 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.09 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" 
            fill="white"
          />
        </svg>
        <div style={styles.downloadButtonTextContainer}>
          <span style={styles.downloadButtonSmallText}>Get Forecast</span>
          <span style={styles.downloadButtonLargeText}>Free iOS Download</span>
        </div>
      </button>
      
      {/* Android Download Button */}
      <button
        onClick={handleAndroidDownload}
        style={styles.androidDownloadButton}
      >
        {/* Google Play Logo SVG */}
        <svg 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "0.5rem" }}
        >
          <path 
            d="M3.18 20.83C3.0633 20.6891 2.99937 20.5246 3 20.36V3.64001C3 3.47001 3.06 3.31001 3.18 3.17001L13.36 12L3.18 20.83Z" 
            fill="white"
          />
          <path 
            d="M16.62 8.99001C16.3664 8.8413 16.0855 8.74681 15.79 8.71001L15.54 8.58001L13.36 12L16.24 15.41L20.92 12.84C19.49 11.99 17.97 10.9 16.62 8.99001Z" 
            fill="white"
          />
          <path 
            d="M13.36 12L3.18 3.17001C3.28 3.03001 3.42 3 3.6 3C3.83 3 4.07 3.08001 4.32 3.21001L13.36 8.48001L15.54 8.58001L13.36 12Z" 
            fill="white"
          />
          <path 
            d="M13.36 15.52L4.32 20.79C4.07 20.92 3.83 21 3.6 21C3.42 21 3.28 20.97 3.18 20.83L13.36 12L16.24 15.41L13.36 15.52Z" 
            fill="white"
          />
        </svg>
        <div style={styles.downloadButtonTextContainer}>
          <span style={styles.downloadButtonSmallText}>Get Forecast</span>
          <span style={styles.downloadButtonLargeText}>Free Android Download</span>
        </div>
      </button>
    </div>
    
    {/* Divider */}
    <div style={styles.divider}>
      <span style={styles.dividerText}>or</span>
    </div>
    
    {/* Start Free Trial Button */}
    <button
      onClick={handleStartTrial}
      style={styles.trialButton}
    >
      <span style={styles.buttonText}>
        Log In
      </span>
    </button>
  </div>
);

return (
  <div style={styles.modalOverlay}>
    {(loading || isForwardingToiOS) ? (
      <LoadingComponent />
    ) : authSuccess ? (
      <div style={styles.keyboardAvoidingContainer}>
        <div style={styles.scrollContainer}>
          <AuthSuccessComponent />
        </div>
      </div>
      ) : isSigningUp && accountCreationDisabled ? (
        <div style={styles.keyboardAvoidingContainer}>
          <div style={styles.scrollContainer}>
            {/* Replace AccountCreationDisabledComponent with WaitlistForm */}
            <div style={styles.waitlistContainer}>
              <h2 style={styles.waitlistHeading}>
                Account Creation Paused 🚫
              </h2>
              
              <p style={styles.waitlistText}>
                We've reached our maximum number of users for the current phase.
              </p>
              
              {/* Integrate the WaitlistForm component */}
              <WaitlistForm />
              
              <div style={styles.loginRedirectContainer}>
                <p style={styles.loginRedirectText}>Already have an account?</p>
                <button 
                  style={styles.loginRedirectButton} 
                  onClick={() => setIsSigningUp(false)}
                >
                  Login Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={styles.keyboardAvoidingContainer}>
          <div style={styles.scrollContainer}>
            <div style={styles.container}>
              {/* Show auth form first if requested, otherwise show download section */}
              {showAuthForm ? (
                <>
                  <h2 style={styles.authFormHeading}>
                    {isSigningUp ? 'Create Account 👋' : 'Login 👋'}
                  </h2>
                  
                  {error && (
                    <div style={styles.errorContainer}>
                      {error}
                    </div>
                  )}

                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      ...styles.input,
                      borderBottomLeftRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                    autoCapitalize="none"
                  />
                  
                  <input
                    ref={passwordRef}
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      ...styles.input,
                      borderRadius: isSigningUp ? 0 : undefined,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      borderBottomLeftRadius: isSigningUp ? 0 : undefined,
                      borderBottomRightRadius: isSigningUp ? 0 : undefined,
                    }}
                    autoCapitalize="none"
                  />
                  
                  {isSigningUp && (
                    <input
                      ref={confirmPasswordRef}
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        ...styles.input,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                      }}
                      autoCapitalize="none"
                    />
                  )}
                  
                  <button
                    style={styles.button}
                    onClick={isSigningUp ? handleCreateAccount : handleSignIn}
                  >
                    <span style={styles.buttonText}>
                      {isSigningUp ? 'Create Account' : 'Login'}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => setIsSigningUp(!isSigningUp)}
                    style={styles.switchModeButton}
                  >
                    {isSigningUp ? 'Already have an account? Login' : "Don't have an account? Sign Up"}
                  </button>
                  
                  {!isSigningUp && (
                    <Link
                      to="/forgot-password"
                      style={styles.forgotPasswordLink}
                    >
                      Forgot your password?
                    </Link>
                  )}
                  
                  <button
                    onClick={() => setShowAuthForm(false)}
                    style={styles.backButton}
                  >
                    ← Back
                  </button>
                </>
              ) : (
                /* Show download section only when auth form is not shown */
                <DownloadButtonsComponent />
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Android Waitlist Modal */}
      {showAndroidModal && (
        <div style={styles.androidModalOverlay}>
          <div style={styles.androidModalContainer}>
            <h3 style={styles.androidModalTitle}>
              Android App Coming Soon
            </h3>
            
            <p style={styles.androidModalText}>
              Our Android app is currently in development. Would you like to be notified when it's available for download?
            </p>
            
            <div style={styles.androidModalButtons}>
              <button
                onClick={() => setShowAndroidModal(false)}
                style={styles.androidModalCancelButton}
              >
                Cancel
              </button>
              
              <button
                onClick={handleAddToWaitlist}
                style={styles.androidModalConfirmButton}
              >
                Notify Me
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// Complete styles object for LoginModal
const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  keyboardAvoidingContainer: {
    width: '90%',
    maxWidth: '400px',
    backgroundColor: 'rgba(255, 255, 255, 0.5)', // Light white overlay
    backdropFilter: 'blur(6px)', // Blur effect
    borderRadius: '50px',

    maxHeight: '90%',
    overflow: 'auto',
  },
  scrollContainer: {
    padding: '24px',
  },
  container: {
    width: '100%',
  },
  largeText: {
    fontSize: '40px',
    fontWeight: '800',
    marginBottom: '24px',
    marginTop: 0,
  },
  // Download section styles
  downloadSection: {
    marginBottom: '24px',
    textAlign: 'center',
  },
  downloadHeading: {
    fontSize: '32px',
    fontWeight: '800',
    marginBottom: '24px',
    marginTop: 0,
  },
  downloadText: {
    fontSize: '14px',
    color: '#64748B',
    marginBottom: '16px',
  },
  downloadButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '12px 16px',
    backgroundColor: 'black',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
  },
  androidDownloadButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    padding: '12px 16px',
    backgroundColor: '#3DDC84',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
  },
  downloadButtonTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  downloadButtonSmallText: {
    fontSize: '13x',
    lineHeight: '1.45',
  },
  downloadButtonLargeText: {
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '1',
  },
  divider: {
    position: 'relative',
    textAlign: 'center',
    margin: '16px 0',
  },
  dividerText: {
    // backgroundColor: 'white',
    color: 'black',
    fontSize: '14px',
    padding: '0 16px',
    position: 'relative',
    zIndex: 1,
  },
  smallAuthButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#64748B',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '12px',
    width: '100%',
    textAlign: 'center',
    marginTop: '8px',
  },
  authFormHeading: {
    fontSize: '40px',
    fontWeight: '800',
    marginBottom: '24px',
    marginTop: 0,
    color: '#0F172A',
    textAlign: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#64748B',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px',
    width: '100%',
    textAlign: 'center',
    marginTop: '16px',
  },
  waitlistSuccessMessage: {
    backgroundColor: '#4CAF50',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '8px',
    marginBottom: '12px',
    textAlign: 'center',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    borderColor: '#e5e5e5',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '16px',
    paddingBottom: '16px',
    marginBottom: '-1px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    height: '60px',
    backgroundColor: 'black',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '16px',
    marginTop: '32px',
    marginBottom: '16px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  buttonText: {
    color: 'white',
    fontSize: '18px',
    fontWeight: '600',
  },
  footerText: {
    color: '#BDBDBD',
    fontSize: '14px',
    textAlign: 'center',
    marginTop: '24px',
    marginBottom: '8px',
  },
  errorContainer: {
    padding: '12px',
    backgroundColor: '#FFEBEE',
    color: '#D32F2F',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  switchModeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#2563eb',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '8px',
    width: '100%',
    textAlign: 'center',
  },
  loadingContainer: {
    width: '90%',
    maxWidth: '400px',
    backgroundColor: 'white',
    borderRadius: '20px',
    height: '300px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '50%',
    borderTopColor: 'black',
    animation: 'spin 1s ease-in-out infinite',
  },
  '@keyframes spin': {
    to: { transform: 'rotate(360deg)' }
  },
  // Success screen styles
  successContainer: {
    textAlign: 'center',
    padding: '16px',
  },
  successHeading: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#0F172A',
  },
  successText: {
    fontSize: '16px',
    color: '#64748B',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  primaryButton: {
    width: '100%',
    height: '60px',
    backgroundColor: '#2563EB',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '16px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  secondaryButton: {
    width: '100%',
    height: '50px',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '16px',
    border: '1px solid #CBD5E1',
    cursor: 'pointer',
  },
  secondaryButtonText: {
    color: '#64748B',
    fontSize: '16px',
    fontWeight: '500',
  },
  trialButton: {
    width: '100%',
    height: '60px',
    backgroundColor: '#007AFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '16px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    marginTop: '8px',
  },
  // Waitlist container styles
  waitlistContainer: {
    width: '100%',
  },
  waitlistHeading: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
    color: '#0F172A',
    textAlign: 'center',
  },
  waitlistText: {
    fontSize: '16px',
    color: '#64748B',
    marginBottom: '24px',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  loginRedirectContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '20px',
    padding: '10px',
    borderTop: '1px solid #E2E8F0',
  },
  loginRedirectText: {
    fontSize: '14px',
    color: '#64748B',
    marginBottom: '8px',
  },
  loginRedirectButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#2563EB',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '8px 16px',
  },
  // Android modal styles
  androidModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  androidModalContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '90%',
    width: '320px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  androidModalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
    color: '#1e293b',
    textAlign: 'center',
  },
  androidModalText: {
    fontSize: '14px',
    color: '#4b5563',
    marginBottom: '20px',
    lineHeight: '1.5',
    textAlign: 'center',
  },
  androidModalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  androidModalCancelButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  androidModalConfirmButton: {
    padding: '8px 16px',
    backgroundColor: '#3DDC84',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
  },
  forgotPasswordLink: {
    backgroundColor: 'transparent',
    color: '#2563eb',
    fontSize: '14px',
    textDecoration: 'none',
    textAlign: 'center',
    display: 'block',
    padding: '8px',
    marginTop: '8px',
  },
};
export default LoginModal;